/**
 * React Query hooks for ICP tests
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/icpTests';
import { apiClient } from '@/api/client';
import { buildApiUrl } from '@/api/config';
import type {
  ICPTestWithRanges,
  ICPTestImport,
  ICPImportResponse,
  ICPImportDefaults,
  ICPFileImportOverrides,
  ElementHistoryResponse,
  ElementDosingRatesResponse,
} from '@/types/sensor';
import type { NearestManualTestData } from '@/api/icpTests';
import { useTank } from '@/contexts/TankContext';
import { CACHE_TIMES } from '@/config/cacheTimes';

/**
 * Fetch a single ICP test with reference ranges
 */
export function useICPTest(barcode: string | undefined) {
  const { selectedTank } = useTank();
  const tankId = selectedTank?.id;

  return useQuery<ICPTestWithRanges, Error>({
    queryKey: ['icp-test', tankId, barcode],
    queryFn: () => {
      if (!barcode) {
        throw new Error('Barcode is required');
      }
      return api.getICPTest(tankId!, barcode);
    },
    enabled: !!barcode && !!tankId,
    staleTime: CACHE_TIMES.DATA,
  });
}

/**
 * Import an ICP test from a pasted results link or barcode.
 * Provider is auto-detected server-side; legacy {barcode, tank_name}
 * payloads are still accepted (Reef Zelements).
 */
export function useImportICPTest() {
  const { selectedTank } = useTank();
  const queryClient = useQueryClient();

  return useMutation<ICPImportResponse, Error, ICPTestImport>({
    mutationFn: (data: ICPTestImport) => {
      if (!selectedTank) {
        throw new Error('No tank selected');
      }
      return api.importICPTest(selectedTank.id, data);
    },
    onSuccess: () => {
      // Invalidate tests list to trigger refresh; a successful import can
      // also remember a provider-side tank label, so refresh defaults too
      queryClient.invalidateQueries({ queryKey: ['icp-tests'] });
      queryClient.invalidateQueries({ queryKey: ['icp-import-defaults'] });
    },
  });
}

/**
 * Import an ICP test from an uploaded report file (ATI PDF / Triton CSV).
 * Optional overrides come from the metadata confirmation step.
 */
export function useImportICPTestFile() {
  const { selectedTank } = useTank();
  const queryClient = useQueryClient();

  return useMutation<
    ICPImportResponse,
    Error,
    { file: File; overrides?: ICPFileImportOverrides }
  >({
    mutationFn: ({ file, overrides }) => {
      if (!selectedTank) {
        throw new Error('No tank selected');
      }
      return api.importICPTestFile(selectedTank.id, file, overrides);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['icp-tests'] });
      queryClient.invalidateQueries({ queryKey: ['icp-import-defaults'] });
    },
  });
}

/**
 * Fetch remembered provider-side tank labels for the import form.
 */
export function useICPImportDefaults(tankId: string | undefined) {
  return useQuery<ICPImportDefaults, Error>({
    queryKey: ['icp-import-defaults', tankId],
    queryFn: () => api.getICPImportDefaults(tankId!),
    enabled: !!tankId,
    staleTime: CACHE_TIMES.DATA,
  });
}

/**
 * Update ICP test metadata (date, tank name)
 */
export function useUpdateICPTest() {
  const { selectedTank } = useTank();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { barcode: string; test_date?: string; tank_name?: string }) => {
      if (!selectedTank) {
        throw new Error('No tank selected');
      }
      const { barcode, ...updateData } = data;
      return api.updateICPTest(selectedTank.id, barcode, updateData);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['icp-test', selectedTank?.id, variables.barcode] });
      queryClient.invalidateQueries({ queryKey: ['icp-tests'] });
      // A changed test date shifts everything computed relative to it:
      // nearest manual test deltas and the element history timeline.
      queryClient.invalidateQueries({ queryKey: ['nearest-manual', selectedTank?.id, variables.barcode] });
      queryClient.invalidateQueries({ queryKey: ['element-history', selectedTank?.id] });
      queryClient.invalidateQueries({ queryKey: ['element-dosing-rates', selectedTank?.id] });
    },
  });
}

interface ParameterSource {
  key: string;
  source: string;
}

/**
 * Fetch available parameter sources for trend linking.
 * Returns a map of parameter key -> list of sources.
 */
export function useAvailableParameterSources() {
  const { selectedTank } = useTank();
  const tankId = selectedTank?.id;

  return useQuery<Record<string, string[]>>({
    queryKey: ['available-param-sources', tankId],
    queryFn: async () => {
      const today = new Date();
      const yearAgo = new Date();
      yearAgo.setFullYear(yearAgo.getFullYear() - 1);
      const formatDate = (d: Date) => d.toISOString().split('T')[0];

      const url = buildApiUrl(
        `/api/tanks/${tankId}/analysis?start_date=${formatDate(yearAgo)}&end_date=${formatDate(today)}&include_dosing=false&include_events=false`
      );
      const response = await apiClient(url, { method: 'GET' });
      if (!response.ok) return {};

      const data = await response.json();
      const bySource: Record<string, string[]> = {};

      for (const ps of (data.available_parameters_by_source || []) as ParameterSource[]) {
        const existing = bySource[ps.key];
        if (!existing) {
          bySource[ps.key] = [ps.source];
        } else {
          existing.push(ps.source);
        }
      }
      return bySource;
    },
    enabled: !!tankId,
    staleTime: CACHE_TIMES.DATA,
  });
}

/**
 * Fetch nearest manual test data for comparison with ICP test.
 * Only fetches when enabled (e.g., comparison checkbox is checked).
 */
export function useNearestManualTests(barcode: string | undefined, enabled: boolean) {
  const { selectedTank } = useTank();
  const tankId = selectedTank?.id;

  return useQuery<NearestManualTestData, Error>({
    queryKey: ['nearest-manual', tankId, barcode],
    queryFn: () => {
      if (!barcode) throw new Error('Barcode required');
      return api.getNearestManualTests(tankId!, barcode);
    },
    enabled: !!barcode && !!tankId && enabled,
    staleTime: CACHE_TIMES.DATA,
  });
}

/**
 * Fetch element history for specified elements
 */
export function useElementHistory(elements: string[]) {
  const { selectedTank } = useTank();
  const tankId = selectedTank?.id;

  return useQuery<ElementHistoryResponse, Error>({
    queryKey: ['element-history', tankId, [...elements].sort().join(',')],
    queryFn: () => {
      if (elements.length === 0) {
        // Return empty response if no elements selected
        return Promise.resolve({
          elements: [],
          test_dates: [],
          total_tests: 0,
        });
      }
      return api.getElementHistory(tankId!, elements);
    },
    enabled: !!tankId && elements.length > 0,
    staleTime: CACHE_TIMES.DATA,
  });
}

/**
 * Fetch trace dosing rates for intervals between ICP test dates
 */
export function useElementDosingRates(testDates: string[]) {
  const { selectedTank } = useTank();
  const tankId = selectedTank?.id;

  // Extract just the date portion (YYYY-MM-DD) from ISO datetime strings
  const dateOnly = testDates.map((d) => d.substring(0, 10));

  return useQuery<ElementDosingRatesResponse, Error>({
    queryKey: ['element-dosing-rates', tankId, dateOnly.join(',')],
    queryFn: () => {
      if (dateOnly.length < 2) {
        return Promise.resolve({ rates: {} });
      }
      return api.getElementDosingRates(tankId!, dateOnly);
    },
    enabled: !!tankId && dateOnly.length >= 2,
    staleTime: CACHE_TIMES.DATA,
  });
}
